<template>
    <div class="rapport-source">
        <b-alert
            show
            variant="info">
            {{ $t('source.fields_with_an_asterisk_are_mandatory_according_to_the_apa_guidelines') }}
        </b-alert>

        <b-form
            v-if="show"
            @submit="onSubmit"
            @reset="onReset">
            <div class="title">
                <b-form-group
                    class="source-title-group"
                    :label="$t('source.title_of_report')"
                    label-for="rapport-source-title">
                    <template #label>
                        <div class="header">
                            <span class="label">{{ $t('source.title_of_report') }}<span class="required">*</span></span>
                            <span
                                class="alert text-danger"
                                v-if="errors.title"
                                role="alert">
                                <i class="fa fa-exclamation-circle"></i>
                                {{ $t('common.please_note') }}: {{ errors.title }}
                            </span>
                        </div>
                    </template>
                    <b-form-input
                        id="rapport-source-title"
                        v-model="form.title"
                        type="text"
                        :placeholder="$t('source.title_of_report')"
                        :class="errors.title && 'is-invalid'" />
                </b-form-group>

                <b-form-group
                    class="title-addition-group"
                    :label="$t('source.addition')"
                    label-for="journal-source-title-addition">
                    <template #label>
                        <div class="header">
                            <span class="label">{{ $t('source.addition') }}<span class="required">*</span></span>
                            <span
                                class="alert text-danger"
                                v-if="errors.addition"
                                role="alert">
                                <i class="fa fa-exclamation-circle"></i>
                                {{ $t('common.please_note') }}: {{ errors.addition }}
                            </span>
                        </div>
                    </template>
                    <b-form-input
                        id="journal-source-title-addition"
                        v-model="form.addition"
                        type="text"
                        placeholder="Bijv. Report No. NCES 2006"
                        :class="errors.addition && 'is-invalid'" />
                </b-form-group>

                <b-form-group
                    class="article-title-group"
                    :label="$t('source.link_to_report')"
                    label-for="journal-source-article-title">
                    <template #label>
                        <div class="header">
                            <span class="label">{{ $t('source.link_to_report') }}<span class="required">*</span></span>
                            <span
                                class="alert text-danger"
                                v-if="errors.url"
                                role="alert">
                                <i class="fa fa-exclamation-circle"></i>
                                {{ $t('common.please_note') }}: {{ errors.url }}
                            </span>
                        </div>
                    </template>
                    <b-form-input
                        id="journal-source-article-title"
                        v-model="form.url"
                        type="text"
                        placeholder="http://www.google.nl/"
                        :class="errors.url && 'is-invalid'" />
                </b-form-group>
            </div>

            <div class="authors">
                <div class="label">
                    Auteur(s):<span class="required">*</span>
                    <span
                        class="alert text-danger"
                        v-if="errors.authors"
                        role="alert">
                        <i class="fa fa-exclamation-circle"></i>
                        {{ $t('common.please_note') }}: {{ errors.authors[0] }}
                    </span>
                    <div class="add-author">
                        <b-button
                            class="btn-add btn-add-author"
                            variant="primary"
                            @click="addAuthor">
                            {{ $t('source.add_author') }}
                        </b-button>
                    </div>
                </div>

                <div
                    class="author"
                    v-if="!form.author_not_available"
                    v-for="(author, index) in form.authors"
                    :key="author.id">
                    <b-form-checkbox
                        class="apprentice-checkbox organisation-check"
                        v-model="form.organisation_check_box[index]">
                        <span>{{ $t('common.organization') }}</span>
                    </b-form-checkbox>

                    <div
                        class="initials"
                        v-if="!form.organisation_check_box[index]">
                        <b-form-input
                            v-model="form.authors[index].author_firstname"
                            type="text"
                            :placeholder="$t('source.initials')" />
                    </div>

                    <div
                        class="insertion"
                        v-if="!form.organisation_check_box[index]">
                        <b-form-input
                            v-model="form.authors[index].author_preposition"
                            type="text"
                            :placeholder="$t('common.infix')" />
                    </div>

                    <div
                        class="surname"
                        v-if="!form.organisation_check_box[index]">
                        <b-form-input
                            v-model="form.authors[index].author_lastname"
                            type="text"
                            :placeholder="$t('common.last_name')" />
                    </div>

                    <div
                        class="organisation"
                        v-if="form.organisation_check_box[index]">
                        <b-form-input
                            v-model="form.authors[index].organisation"
                            type="text"
                            :placeholder="$t('common.organization')" />
                    </div>

                    <div class="remove-author">
                        <b-button
                            class="btn-remove btn-remove-author d-none d-md-flex remove-icon-margin"
                            variant="danger-icon"
                            @click="removeAuthor(index)">
                        </b-button>
                        <b-button
                            class="btn-remove-author d-flex d-md-none"
                            variant="danger"
                            @click="removeAuthor(index)">
                            <i class="btn-remove"></i>{{ $t('source.delete_author') }}
                        </b-button>
                    </div>
                </div>
            </div>

            <div class="publication-year">
                <b-form-group
                    :label="$t('source.publication_year')"
                    label-for="paper-source-publish-year">
                    <template #label>
                        <div class="header">
                            <span class="label"
                                >{{ $t('source.publication_year') }}<span class="required">*</span></span
                            >
                            <span
                                class="alert text-danger"
                                v-if="errors.publication_year"
                                role="alert">
                                <i class="fa fa-exclamation-circle"></i>
                                {{ $t('common.please_note') }}: {{ errors.publication_year[0] }}
                            </span>
                        </div>
                    </template>
                    <b-form-input
                        id="paper-source-publish-year"
                        v-model="form.publication_year"
                        type="number"
                        :disabled="form.publication"
                        :placeholder="currentYear.toString()"
                        :class="errors.publication_year && 'is-invalid'" />
                </b-form-group>
            </div>

            <div class="paper-source-button-groups">
                <b-button
                    class="btn-save btn-source-book-save"
                    v-if="form.id == ''"
                    type="submit"
                    variant="primary">
                    {{ $t('source.save_source') }}
                </b-button>

                <b-button
                    class="btn-edit btn-source-book-edit"
                    v-else
                    type="submit"
                    variant="primary">
                    {{ $t('source.edit_source') }}
                </b-button>

                <b-button
                    class="btn-reset btn-source-book-reset-form"
                    type="reset"
                    variant="outline-primary">
                    {{ $t('source.clear_form') }}
                </b-button>
            </div>
        </b-form>
    </div>
</template>

<script>
import { SourceBus } from '../../app';

export default {
    name: 'design-2021-source-rapport-form',
    props: ['projectId'],
    data() {
        return {
            currentYear: new Date().getFullYear(),
            form: {
                base: 'RapportSource',
                id: '',
                title: '',
                addition: '',
                url: '',
                publication_year: '',
                checked: [],
                authors: [],
                organisation_check_box: [],
            },
            show: true,
            errors: [],
        };
    },
    methods: {
        onSubmit(evt) {
            evt.preventDefault();

            this.errors = [];

            this.checkYearInput();

            if (this.errors.length) {
                return;
            }

            let uri = '/source/store/' + this.projectId;
            let data = this.form;

            axios
                .post(uri, data)
                .then((response) => {
                    if (response.data) {
                        this.alert.success();
                        this.onReset(evt);

                        // Add source to overview.
                        SourceBus.$emit('pushData', response.data);
                    }
                })
                .catch((error) => {
                    if (error.response.status == 422) {
                        this.errors = error.response.data.errors;
                        this.checkYearInput();
                    }
                });
        },

        addAuthor() {
            this.form.authors.push({
                author_firstname: '',
                author_preposition: '',
                author_lastname: '',
                organisation: '',
            });
        },

        removeAuthor(index) {
            this.form.authors.splice(index, 1);
        },

        editField(data) {
            SourceBus.$emit('updateTabs', this.$t('source.report'));

            if (data.rapport_title) {
                this.form.title = data.rapport_title;
            }

            if (data.rapport_url) {
                this.form.url = data.rapport_url;
            }

            if (data.rapport_addition) {
                this.form.addition = data.rapport_addition;
            }

            if (data.rapport_publication_year) {
                this.form.publication_year = data.rapport_publication_year;
            }

            if (data.id) {
                this.form.id = data.id;
            }

            if (data.authors) {
                this.form.authors = [];
                data.authors.forEach((value, index) => {
                    // Default Author
                    if (!value.organisation) {
                        this.form.authors.push({
                            author_firstname: value.initials,
                            author_preposition: value.preposition,
                            author_lastname: value.last_name,
                            organisation: value.organisation,
                        });
                    }

                    // Organisation
                    else {
                        this.form.organisation_check_box[index] = true;

                        this.form.authors.push({
                            author_firstname: value.initials,
                            author_preposition: value.preposition,
                            author_lastname: value.last_name,
                            organisation: value.organisation,
                        });
                    }
                });
            }
        },

        onReset(evt) {
            evt.preventDefault();
            /* Reset our form values */
            this.form.title = '';
            this.form.addition = '';
            this.form.url = '';
            this.form.publication_year = '';
            this.form.authors = [];
            this.form.id = '';

            /* Trick to reset/clear native browser form validation state */
            this.show = false;
            this.$nextTick(() => {
                this.show = true;
            });
        },

        checkYearInput() {
            if (
                this.form.publication_year !== '' &&
                (Array.from(String(this.form.publication_year), Number).length !== 4 ||
                    isNaN(parseInt(this.form.publication_year)))
            ) {
                this.errors.publication_year = [this.$t('source.year_of_publication_must_be_between_1970_and_9099')];
            }
        },
    },

    created() {
        SourceBus.$on('editRapportSource', (data) => {
            this.editField(data);
        });
    },
};
</script>

<style scoped lang="scss">
.form-group {
    .header {
        flex-direction: column;
        flex-wrap: wrap;
        align-items: flex-start;
        justify-content: space-between;

        .alert {
            padding: 0;
            margin-bottom: 0;
        }
    }
}

.title {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    flex-wrap: wrap;
    margin-bottom: map-get($grid-gutter-spacing, 'fifteen');

    .article-title-group {
        margin-bottom: 0;
    }

    .form-control {
        margin-bottom: 0;
    }
}

.authors {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    margin-bottom: map-get($grid-gutter-spacing, 'fifteen');

    .label {
        display: flex;
        flex: 1;
        flex-wrap: wrap;
        font-weight: $font-weight-medium;
        margin-bottom: map-get($grid-gutter-spacing, 'ten');

        .alert {
            padding: 0;
            margin-bottom: 0;
            margin-left: auto;
        }

        .add-author {
            display: flex;
            flex: 1 0 100%;
            justify-content: flex-end;
        }
    }

    .organisation {
        margin-bottom: map-get($grid-gutter-spacing, 'ten');
    }

    .author {
        width: 100%;
        border-bottom: 1px solid $border-color;
        padding-bottom: map-get($grid-gutter-spacing, 'ten');

        > div:not(:last-child) {
            margin-bottom: map-get($grid-gutter-spacing, 'eight');
        }

        .form-control {
            margin-bottom: 0;
        }

        &:not(:last-child) {
            margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
        }

        .remove-author {
            display: flex;
            justify-content: flex-end;

            .btn-remove-author {
                display: flex;
                flex-direction: row;
                align-items: center;

                i {
                    margin-right: map-get($grid-gutter-spacing, 'eight');
                }
            }
        }
    }
}

.paper-source-button-groups {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    margin-top: $grid-gutter-width;
    margin-bottom: $grid-gutter-width;

    button {
        &:not(:last-child) {
            margin-bottom: map-get($grid-gutter-spacing, 'ten');
        }
    }
}

@media (min-width: map-get($grid-breakpoints, 'md')) {
    .form-group {
        .header {
            flex-direction: row;
            align-items: center;
        }
    }

    .title {
        flex-direction: row;

        .source-title-group,
        .title-addition-group {
            flex: 1 1 calc(50% - 15px);

            &:first-child {
                margin-right: 30px;
            }
        }

        .article-title-group {
            flex: 1 1 100%;
        }
    }

    .authors {
        .label {
            align-items: center;
            flex-wrap: nowrap;

            .add-author {
                flex: initial;
                justify-content: initial;
                margin-left: auto;
            }
        }

        .author-unknown {
            flex-direction: row;
            align-items: center;

            .author-unknown-check {
                margin-bottom: 0;
            }
        }

        .author {
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            justify-content: space-between;
            align-items: baseline;

            .organisation-check {
                flex: 1 1 100%;
            }

            .initials,
            .surname {
                min-width: 165px;
            }

            .insertion {
                width: 125px;
            }

            .form-control {
                margin-bottom: 0;
            }
        }
    }

    .paper-source-button-groups {
        flex-direction: row;

        button {
            &:not(:last-child) {
                margin-right: map-get($grid-gutter-spacing, 'fifteen');
                margin-bottom: 0;
            }
        }
    }
}

@media (min-width: map-get($grid-breakpoints, 'xl')) {
    form {
        max-width: 55%;
    }

    .authors {
        .author {
            .initials,
            .surname {
                flex: 1;
            }

            .surname {
                margin-right: map-get($grid-gutter-spacing, 'fifteen');
            }

            .insertion {
                margin-right: $grid-gutter-width;
                margin-left: $grid-gutter-width;
            }
        }
    }
}
</style>
